import { request } from './apiClient';
import authStorage from '../utils/authStorage';

export const vendorService = {
  // Categories & Tags
  getCategories: async (all = false, categoryType = '', section = '') => {
    const params = new URLSearchParams();
    if (all) params.append('all', 'true');
    if (categoryType) params.append('categoryType', categoryType);
    if (section) params.append('section', section);
    const queryString = params.toString();
    return request(`/vendor/categories${queryString ? '?' + queryString : ''}`, { method: 'GET' });
  },

  getTagsByCategory: async (categoryId) => {
    return request(`/vendor/tags/category/${categoryId}`, { method: 'GET' });
  },

  getBrandsByCategory: async (categoryId) => {
    return request(`/vendor/brands/category/${categoryId}`, { method: 'GET' });
  },

  createCategory: async (categoryData) => {
    return request('/vendor/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData)
    });
  },

  updateCategory: async (id, categoryData) => {
    return request(`/vendor/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData)
    });
  },

  deleteCategory: async (id) => {
    return request(`/vendor/categories/${id}`, {
      method: 'DELETE'
    });
  },

  updateTagStatus: async (tagId, isActive) => {
    return request(`/vendor/tags/${tagId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ isActive })
    });
  },

  // Listings
  createListing: async (formData) => {
    return request('/vendor/listings', {
      method: 'POST',
      body: formData, // passing FormData directly
    });
  },

  updateListing: async (id, formData) => {
    return request(`/vendor/listings/${id}`, {
      method: 'PUT',
      body: formData,
    });
  },

  getMyListings: async (page = 0, size = 10) => {
    return request(`/vendor/listings?page=${page}&size=${size}`, { method: 'GET' });
  },

  getListingById: async (id) => {
    return request(`/vendor/listings/${id}`, { method: 'GET' });
  },

  deleteMyListing: async (id) => {
    return request(`/vendor/listings/${id}`, { method: 'DELETE' });
  },


  uploadLogo: async (logoFormData) => {
    return request('/vendor/listings/logo', {
      method: 'POST',
      body: logoFormData,
    });
  },

  uploadProfilePhoto: async (formData) => {
    return request('/vendor/profile/photo', {
      method: 'POST',
      body: formData,
    });
  },


  getPublicLocations: async () => {
    return request('/vendor/listings/public/locations', { method: 'GET' });
  },

  getPublicListings: async (filters = {}, page = 0, size = 12) => {
    const params = new URLSearchParams();
    if (filters.q) params.append('q', filters.q);
    if (filters.state) params.append('state', filters.state);
    if (filters.city) params.append('city', filters.city);
    if (filters.categoryId) params.append('categoryId', filters.categoryId);
    if (filters.category) params.append('category', filters.category);
    if (filters.categoryName) params.append('categoryName', filters.categoryName);
    if (filters.tagId) params.append('tagId', filters.tagId);
    if (filters.tagIds) params.append('tagIds', filters.tagIds);
    if (filters.brandId) params.append('brandId', filters.brandId);
    if (filters.brandIds) params.append('brandIds', filters.brandIds);
    if (filters.section) params.append('section', filters.section);
    params.append('page', page);
    params.append('size', size);
    return request(`/vendor/listings/public?${params.toString()}`, { method: 'GET' });
  },

  getPublicListingById: async (id) => {
    return request(`/vendor/listings/public/${id}`, { method: 'GET' });
  },

  recordSessionTime: (listingId, durationSeconds) => {
    // Uses navigator.sendBeacon for reliable delivery on tab close / page navigation.
    // IMPORTANT: sendBeacon cannot send custom headers (no Authorization / x-auth-user),
    // so the logged-in user's email must be included in the JSON body itself.
    const url = `/api/vendor/listings/public/${listingId}/session-time`;

    // Read logged-in user's email from authStorage (cookie-based, always available)
    let visitorEmail = null;
    try {
      const stored = authStorage.getItem('user');
      if (stored) {
        const userObj = JSON.parse(stored);
        visitorEmail = userObj?.emailId || userObj?.email || null;
      }
    } catch (_) {}

    const payload = JSON.stringify({
      duration_seconds: Math.round(durationSeconds),
      visitor_email: visitorEmail,    // null for guests, email string for logged-in users
    });
    const blob = new Blob([payload], { type: 'application/json' });

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(url, blob);
    } else {
      // Fallback for environments without sendBeacon support
      fetch(url, {
        method: 'POST',
        body: payload,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => {}); // Silently ignore errors
    }
  },

  getCaptcha: async () => {
    return request('/vendor/captcha', { method: 'GET' });
  },

  createInquiry: async (listingId, inquiryData) => {
    return request(`/vendor/listings/${listingId}/inquiries`, {
      method: 'POST',
      body: JSON.stringify(inquiryData)
    });
  },

  getMyInquiries: async (filters = {}, page = 0, size = 10) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.keyword) params.append('keyword', filters.keyword);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    params.append('limit', size);
    params.append('offset', page * size);
    return request(`/vendor/listings/my/inquiries?${params.toString()}`, { method: 'GET' });
  },

  updateInquiryStatus: async (inquiryId, status) => {
    return request(`/vendor/listings/inquiries/${inquiryId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  },

  getAdminNotificationCounts: async (includeList = false) => {
    const url = includeList
      ? '/vendor/admin/notifications/count?include_list=true'
      : '/vendor/admin/notifications/count';
    return request(url, { method: 'GET' });
  },

  getAdminOverviewStats: async () => {
    return request('/vendor/admin/overview/stats', { method: 'GET' });
  },

  getAdminListings: async (status = '', keyword = '', page = 0, size = 10, isUpdated = null, isClaimed = null, summary = null, startDate = '', endDate = '', categoryId = '', city = '', listingType = '') => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (keyword) params.append('keyword', keyword);
    if (isUpdated !== null) params.append('isUpdated', isUpdated);
    if (isClaimed !== null) params.append('isClaimed', isClaimed);
    if (summary !== null) params.append('summary', summary);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (categoryId) params.append('categoryId', categoryId);
    if (city) params.append('city', city);
    if (listingType) params.append('listingType', listingType);
    params.append('page', page);
    params.append('size', size);
    return request(`/vendor/admin/listings?${params.toString()}`, { method: 'GET' });
  },

  getAdminListingCities: async () => {
    return request('/vendor/admin/listings/cities', { method: 'GET' });
  },

  adminUpdateUnclaimedListing: async (id, formData) => {
    return request(`/vendor/admin/listings/${id}`, {
      method: 'PUT',
      body: formData,
    });
  },

  adminDeleteUnclaimedListing: async (id) => {
    return request(`/vendor/admin/listings/${id}`, {
      method: 'DELETE',
    });
  },

  updateListingStatus: async (id, status, rejectReason = null) => {
    return request(`/vendor/admin/listings/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, rejectReason })
    });
  },

  getUsersNoListing: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.append('page', params.page);
    if (params.size !== undefined) query.append('size', params.size);
    if (params.search) query.append('search', params.search);
    if (params.leadStatus) query.append('lead_status', params.leadStatus);
    if (params.signupAs) query.append('signup_as', params.signupAs);
    if (params.startDate) query.append('startDate', params.startDate);
    if (params.endDate) query.append('endDate', params.endDate);
    const queryString = query.toString();
    return request(`/vendor/admin/users-no-listing${queryString ? '?' + queryString : ''}`, { method: 'GET' });
  },

  sendManualListingReminder: async (userId, reminderType = '23hr') => {
    return request(`/vendor/admin/users-no-listing/${userId}/send-reminder?reminder_type=${reminderType}`, {
      method: 'POST'
    });
  },

  broadcastAdminWhatsApp: async (payload) => {
    return request('/vendor/admin/listings/broadcast-whatsapp', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  broadcastAdminEmail: async (payload) => {
    return request('/vendor/admin/listings/broadcast-email', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  getAdminMemberships: async () => {
    return request('/vendor/admin/memberships', { method: 'GET' });
  },

  cancelAdminMembershipAutoPay: async (membershipId) => {
    return request(`/vendor/admin/memberships/${membershipId}/cancel-autopay`, { method: 'POST' });
  },

  updateLeadStatus: async (vendorEmail, leadStatus) => {
    return request('/vendor/admin/memberships/lead-status', {
      method: 'PUT',
      body: JSON.stringify({ vendor_email: vendorEmail, lead_status: leadStatus })
    });
  },

  getAdminInquiries: async (filters, page = 0, size = 25) => {
    const params = new URLSearchParams();
    if (filters.vendorEmail) params.append('vendor_email', filters.vendorEmail);
    if (filters.startDate) params.append('start_date', filters.startDate);
    if (filters.endDate) params.append('end_date', filters.endDate);
    if (filters.city) params.append('city', filters.city);
    if (filters.category_id) params.append('category_id', filters.category_id);
    params.append('page', page);
    params.append('size', size);
    return request(`/vendor/admin/inquiries?${params.toString()}`, { method: 'GET' });
  },

  getAdminAllVendors: async () => {
    return request('/vendor/admin/all-vendors', { method: 'GET' });
  },

  getAdminManualMemberships: async () => {
    return request('/vendor/admin/memberships/manual', { method: 'GET' });
  },

  createManualMembership: async (payload) => {
    return request('/vendor/admin/memberships/manual', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  updateManualMembership: async (id, payload) => {
    return request(`/vendor/admin/memberships/manual/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  },

  getAdminMembershipConfig: async () => {
    return request('/vendor/admin/memberships/config', { method: 'GET' });
  },

  updateAdminMembershipConfig: async (payload) => {
    return request('/vendor/admin/memberships/config', {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
  },

  getPublicMembershipPlans: async (listingType) => {
    const qs = listingType ? `?listing_type=${encodeURIComponent(listingType)}` : '';
    return request(`/vendor/membership/plans${qs}`, { method: 'GET' });
  },

  // ── Vendor Membership (Razorpay) ────────────────────────────────────
  getCurrentMembership: async () => {
    return request('/vendor/membership/current', { method: 'GET' });
  },

  createMembershipOrder: async (planName, listingType) => {
    return request('/vendor/membership/create-order', {
      method: 'POST',
      body: JSON.stringify({ plan_name: planName, listing_type: listingType })
    });
  },

  verifyMembershipPayment: async (payload) => {
    return request('/vendor/membership/verify-payment', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  },

  cancelMembershipAutoPay: async () => {
    return request('/vendor/membership/cancel-autopay', {
      method: 'POST'
    });
  },


  getAdminSupportTickets: async (filters = {}, page = 0, size = 10) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.ticketType) params.append('ticketType', filters.ticketType);
    if (filters.search) params.append('search', filters.search);
    params.append('page', page);
    params.append('size', size);
    return request(`/vendor/admin/support/tickets?${params.toString()}`, { method: 'GET' });
  },

  createSupportTicket: async (formData) => {
    return request('/vendor/support/tickets', {
      method: 'POST',
      body: formData // Passing FormData directly
    });
  },

  getMySupportTickets: async (page = 0, size = 5) => {
    return request(`/vendor/support/tickets?page=${page}&size=${size}`, { method: 'GET' });
  },

  updateSupportTicketStatus: async (id, status) => {
    return request(`/vendor/admin/support/tickets/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  },

  replySupportTicket: async (id, reply, status = null) => {
    return request(`/vendor/admin/support/tickets/${id}/reply`, {
      method: 'PUT',
      body: JSON.stringify({ reply, status })
    });
  },

  replySupportTicketUser: async (id, reply) => {
    return request(`/vendor/support/tickets/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ reply })
    });
  },

  uploadExcelListings: async (formData) => {
    return request('/vendor/admin/listings/upload-excel', {
      method: 'POST',
      body: formData
    });
  },

  checkExistingListing: async (email, phone) => {
    return request(`/vendor/listings/public/check-existing?email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}`, {
      method: 'GET'
    });
  },

  claimListing: async (listingId, formData) => {
    return request(`/vendor/listings/${listingId}/claim`, {
      method: 'POST',
      body: formData
    });
  },

  getMyClaims: async () => {
    return request('/vendor/listings/my/claims', {
      method: 'GET'
    });
  },

  getAdminClaims: async (page = 0, size = 10, status = 'ALL', startDate = '', endDate = '') => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('size', size);
    params.append('status', status);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return request(`/vendor/admin/claims?${params.toString()}`, {
      method: 'GET'
    });
  },

  updateClaimStatus: async (claimId, status, rejectReason = null) => {
    return request(`/vendor/admin/claims/${claimId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status, rejectReason })
    });
  },

  // ── Reviews and Reports ─────────────────────────────────────────────
  getListingReviews: async (listingId) => {
    return request(`/vendor/listings/public/${listingId}/reviews`, { method: 'GET' });
  },

  createReview: async (listingId, rating, comment) => {
    return request(`/vendor/listings/${listingId}/reviews`, {
      method: 'POST',
      body: JSON.stringify({ rating, comment })
    });
  },

  getReceivedReviews: async () => {
    return request('/vendor/reviews/received', { method: 'GET' });
  },

  getSentReviews: async () => {
    return request('/vendor/reviews/sent', { method: 'GET' });
  },

  replyToReview: async (reviewId, reply) => {
    return request(`/vendor/reviews/${reviewId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ reply })
    });
  },

  reportReview: async (reviewId, reason, sub_reason = null) => {
    return request(`/vendor/reviews/${reviewId}/report`, {
      method: 'POST',
      body: JSON.stringify({ reason, sub_reason })
    });
  },

  getReportedReviewsAdmin: async () => {
    return request('/vendor/admin/reviews/reported', { method: 'GET' });
  },

  deleteReviewAdmin: async (reviewId) => {
    return request(`/vendor/admin/reviews/${reviewId}`, { method: 'DELETE' });
  },

  dismissReportAdmin: async (reportId) => {
    return request(`/vendor/admin/reports/${reportId}`, { method: 'DELETE' });
  },

  // ── Magazine (User-facing) ────────────────────────────────────────────────

  /** Public — get magazine config: pricing, current edition month/year, sample PDF URL */
  getMagazineConfig: async () => {
    return request('/vendor/magazine/config', { method: 'GET' });
  },

  /** Auth-required — get logged-in user's active yearly magazine subscription */
  getMyMagazineSubscription: async () => {
    return request('/vendor/magazine/my-subscription', { method: 'GET' });
  },

  /**
   * Auth-required — create a Razorpay order.
   * payload: { type: 'YEARLY'|'COPY', quantity?, delivery_address, pincode, name?, phone? }
   */
  createMagazineOrder: async (payload) => {
    return request('/vendor/magazine/create-order', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Auth-required — verify Razorpay payment and activate subscription/order.
   * payload: { razorpay_order_id, razorpay_payment_id, razorpay_signature, type }
   */
  verifyMagazinePayment: async (payload) => {
    return request('/vendor/magazine/verify-payment', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /** Auth-required — get all magazine payment history (yearly + copy orders) */
  getMagazinePaymentHistory: async () => {
    return request('/vendor/magazine/payment-history', { method: 'GET' });
  },

  /** Auth-required — cancel the logged-in user's active AutoPay mandate */
  cancelMagazineAutoPay: async () => {
    return request('/vendor/magazine/cancel-autopay', { method: 'POST' });
  },


  // ── Magazine (Admin) ──────────────────────────────────────────────────────

  /** Admin — get current magazine config */
  getAdminMagazineConfig: async () => {
    return request('/vendor/admin/magazine/config', { method: 'GET' });
  },

  /**
   * Admin — update magazine config (partial update).
   * data: { yearly_price?, copy_price?, recent_month?, recent_year?, is_active? }
   */
  updateAdminMagazineConfig: async (data) => {
    return request('/vendor/admin/magazine/config', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  /** Admin — upload sample magazine PDF (takes File object, packs in FormData) */
  uploadAdminMagazineSamplePdf: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request('/vendor/admin/magazine/sample-pdf', {
      method: 'POST',
      body: formData,
    });
  },

  /** Admin — delete sample magazine PDF */
  deleteAdminMagazineSamplePdf: async () => {
    return request('/vendor/admin/magazine/sample-pdf', {
      method: 'DELETE',
    });
  },

  /** Admin — cancel subscriber AutoPay subscription */
  cancelAdminMagazineSubscriberAutoPay: async (subscriptionId) => {
    return request(`/vendor/admin/magazine/subscribers/${subscriptionId}/cancel-autopay`, {
      method: 'POST',
    });
  },


  /** Admin — get magazine overview counts & chart trend data */
  getAdminMagazineOverview: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    return request(`/vendor/admin/magazine/overview?${params.toString()}`, { method: 'GET' });
  },

  /** Admin — list magazine subscribers (paginated, 25 records, default descending) */
  getAdminMagazineSubscribers: async (filters = {}) => {
    const params = new URLSearchParams();
    params.append('page', filters.page || 1);
    params.append('page_size', filters.page_size || 25);
    params.append('status_tab', filters.status_tab || 'active');
    params.append('type_filter', filters.type_filter || 'ALL');
    if (filters.search) params.append('search', filters.search);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    return request(`/vendor/admin/magazine/subscribers?${params.toString()}`, { method: 'GET' });
  },

  /** Admin — list per-copy print issue orders (paginated, 25 records, default descending) */
  getAdminMagazineCopyOrders: async (filters = {}) => {
    const params = new URLSearchParams();
    params.append('page', filters.page || 1);
    params.append('page_size', filters.page_size || 25);
    params.append('delivery_status', filters.delivery_status || 'ALL');
    if (filters.search) params.append('search', filters.search);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    return request(`/vendor/admin/magazine/copy-orders?${params.toString()}`, { method: 'GET' });
  },

  /** Admin — update print copy order delivery fulfillment status */
  updateCopyOrderDelivery: async (orderId, delivery_status) => {
    return request(`/vendor/admin/magazine/copy-orders/${orderId}/delivery?delivery_status=${delivery_status}`, {
      method: 'PUT'
    });
  },

  /** Admin — create manual subscriber (custom price, 1-year) or guest (1-year/lifetime) and send email */
  createManualSubscription: async (data) => {
    return request('/vendor/admin/magazine/manual', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  /** Admin — update manually granted subscriber/guest details */
  updateManualSubscription: async (id, data) => {
    return request(`/vendor/admin/magazine/manual/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  /** Admin — list manual subscriptions grants history logs (paginated, 25 records, default descending) */
  getManualSubscriptionsList: async (filters = {}) => {
    const params = new URLSearchParams();
    params.append('page', filters.page || 1);
    params.append('page_size', filters.page_size || 25);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    return request(`/vendor/admin/magazine/manual-list?${params.toString()}`, { method: 'GET' });
  },

  /** Admin — autocomplete autocomplete autocomplete user database search */
  searchSystemUsers: async (q) => {
    return request(`/vendor/admin/magazine/users-search?q=${q}`, { method: 'GET' });
  },

  // ── Admin Vendor Listing Manager ──
  getAdminVendorsForListingManager: async () => {
    return request('/vendor/admin/vendor-listing-manager/vendors', { method: 'GET' });
  },

  getAdminVendorListing: async (vendorEmail) => {
    return request(`/vendor/admin/vendor-listing-manager/vendor-listing/${encodeURIComponent(vendorEmail)}`, { method: 'GET' });
  },

  adminSubmitVendorListingDraft: async (formData) => {
    return request('/vendor/admin/vendor-listing-manager/draft', {
      method: 'POST',
      body: formData,
    });
  },

  getAdminListingDrafts: async (filters = {}, page = 0, size = 20) => {
    const params = new URLSearchParams();
    if (filters.vendorEmail) params.append('vendorEmail', filters.vendorEmail);
    if (filters.status) params.append('status', filters.status);
    params.append('page', page);
    params.append('size', size);
    return request(`/vendor/admin/vendor-listing-manager/drafts?${params.toString()}`, { method: 'GET' });
  },

  cancelAdminListingDraft: async (draftId) => {
    return request(`/vendor/admin/vendor-listing-manager/draft/${draftId}`, { method: 'DELETE' });
  },

  adminDeleteVendorListing: async (listingId) => {
    return request(`/vendor/admin/vendor-listing-manager/delete-listing/${listingId}`, { method: 'DELETE' });
  },

  // Vendor — Draft Response
  getMyPendingAdminDraft: async () => {
    return request('/vendor/listings/my-pending-admin-draft', { method: 'GET' });
  },

  respondToAdminDraft: async (draftId, decision, rejectReason = null) => {
    return request(`/vendor/listings/admin-draft/${draftId}/decision`, {
      method: 'POST',
      body: JSON.stringify({ decision, rejectReason })
    });
  },

  // ── Admin Vendor Onboarding Video Settings ──
  getWelcomeYoutubeUrl: async () => {
    return request('/vendor/admin/onboarding/youtube-url', { method: 'GET' });
  },

  updateWelcomeYoutubeUrl: async (youtubeUrl) => {
    return request('/vendor/admin/onboarding/youtube-url', {
      method: 'PUT',
      body: JSON.stringify({ youtubeUrl })
    });
  },

  // ── City-Category Market Density & Analytics ──
  getCityCategoryDensityReport: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.min_count !== undefined && params.min_count !== null) query.append('min_count', params.min_count);
    if (params.city) query.append('city', params.city);
    if (params.category_id) query.append('category_id', params.category_id);
    if (params.category_type) query.append('category_type', params.category_type);
    if (params.categoryType) query.append('category_type', params.categoryType);
    if (params.plan) query.append('plan', params.plan);
    if (params.start_date) query.append('start_date', params.start_date);
    if (params.end_date) query.append('end_date', params.end_date);
    if (params.status_filter) query.append('status_filter', params.status_filter);
    const queryString = query.toString();
    return request(`/vendor/admin/analytics/city-category-density${queryString ? `?${queryString}` : ''}`, { method: 'GET' });
  },

  getCityCategoryVendors: async (params = {}) => {
    const query = new URLSearchParams();
    if (params.city) query.append('city', params.city);
    if (params.category_id) query.append('category_id', params.category_id);
    if (params.category_type) query.append('category_type', params.category_type);
    if (params.categoryType) query.append('category_type', params.categoryType);
    if (params.status_filter) query.append('status_filter', params.status_filter);
    return request(`/vendor/admin/analytics/city-category-density/vendors?${query.toString()}`, { method: 'GET' });
  },

  // Favorites
  toggleFavorite: async (listingId) => {
    return request(`/vendor/favorites/toggle/${listingId}`, { method: 'POST' });
  },

  checkFavorite: async (listingId) => {
    return request(`/vendor/favorites/check/${listingId}`, { method: 'GET' });
  },

  getFavoriteIds: async () => {
    return request('/vendor/favorites/ids', { method: 'GET' });
  },

  getMyFavorites: async (page = 0, size = 12, filters = {}) => {
    let queryParams = `page=${page}&size=${size}`;
    if (filters.state) queryParams += `&state=${encodeURIComponent(filters.state)}`;
    if (filters.city) queryParams += `&city=${encodeURIComponent(filters.city)}`;
    if (filters.categoryId) queryParams += `&category_id=${encodeURIComponent(filters.categoryId)}`;
    return request(`/vendor/favorites?${queryParams}`, { method: 'GET' });
  },

  // ── Vendor Performance Analytics & Telemetry ──────────────────────────────
  trackListingEvent: (listingId, eventType, sourcePage = 'DETAILS') => {
    if (!listingId || !eventType) return;
    try {
      let visitorEmail = null;
      const stored = authStorage.getItem('user');
      if (stored) {
        try {
          const userObj = JSON.parse(stored);
          visitorEmail = userObj?.emailId || userObj?.email || null;
        } catch (_) {}
      }
      const payload = JSON.stringify({
        event_type: eventType,
        source_page: sourcePage,
        visitor_email: visitorEmail
      });
      const url = `/api/vendor/listings/public/${listingId}/track-event`;
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: 'POST',
          body: payload,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true
        }).catch(() => {});
      }
    } catch (_) {}
  },

  trackBatchImpressions: (listingIds, sourcePage = 'SEARCH') => {
    if (!listingIds || !Array.isArray(listingIds) || listingIds.length === 0) return;
    try {
      let visitorEmail = null;
      const stored = authStorage.getItem('user');
      if (stored) {
        try {
          const userObj = JSON.parse(stored);
          visitorEmail = userObj?.emailId || userObj?.email || null;
        } catch (_) {}
      }
      const payload = JSON.stringify({
        listing_ids: listingIds,
        source_page: sourcePage,
        visitor_email: visitorEmail
      });
      const url = `/api/vendor/listings/public/batch-impressions`;
      if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(url, blob);
      } else {
        fetch(url, {
          method: 'POST',
          body: payload,
          headers: { 'Content-Type': 'application/json' },
          keepalive: true
        }).catch(() => {});
      }
    } catch (_) {}
  },

  getVendorDashboardAnalytics: async (startDate = null, endDate = null) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    const qs = params.toString();
    return request(`/vendor/analytics/dashboard${qs ? '?' + qs : ''}`, { method: 'GET' });
  },

  // ── SEO Management ────────────────────────────────────────────────────────
  getAdminSeoList: async (page = 0, size = 15, search = '', pageType = '', isActive = null) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('size', size);
    if (search) params.append('search', search);
    if (pageType && pageType !== 'ALL') params.append('page_type', pageType);
    if (isActive !== null && isActive !== '') params.append('is_active', isActive);
    return request(`/vendor/admin/seo?${params.toString()}`, { method: 'GET' });
  },

  getAdminSeoById: async (id) => {
    return request(`/vendor/admin/seo/${id}`, { method: 'GET' });
  },

  createAdminSeo: async (seoData) => {
    return request('/vendor/admin/seo', {
      method: 'POST',
      body: JSON.stringify(seoData),
    });
  },

  updateAdminSeo: async (id, seoData) => {
    return request(`/vendor/admin/seo/${id}`, {
      method: 'PUT',
      body: JSON.stringify(seoData),
    });
  },

  toggleAdminSeoStatus: async (id) => {
    return request(`/vendor/admin/seo/${id}/status`, {
      method: 'PATCH',
    });
  },

  deleteAdminSeo: async (id) => {
    return request(`/vendor/admin/seo/${id}`, {
      method: 'DELETE',
    });
  },

  getAdminSeoQuickRoutes: async () => {
    return request('/vendor/admin/seo/quick-routes', { method: 'GET' });
  },

  getAdminSeoSuggestions: async (query) => {
    const params = new URLSearchParams({ q: query });
    return request(`/vendor/admin/seo/suggestions?${params.toString()}`, { method: 'GET' });
  },

  resolveSeoMetadata: async (urlPath) => {
    const encoded = encodeURIComponent(urlPath);
    return request(`/vendor/seo/resolve?url=${encoded}`, { method: 'GET' });
  },
};

export default vendorService;



